import { z } from 'zod'
import { tenantFeatureSchema } from '../enums/tenant-feature.enum.js'
import { instantSchema } from '../validators/temporal.validator.js'
import { billingOwnerDtoSchema } from './billing-owner.dto.js'
import { billingSummaryDtoSchema } from './billing-summary.dto.js'

/**
 * A tenant (an organization on the platform). Managed only by a platform
 * SUPER_ADMIN; tenant admins never see this shape. Counts summarize what the
 * tenant contains for the super-admin list.
 */
export const tenantDtoSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  createdAt: instantSchema,
  venueCount: z.number().int().nonnegative(),
  userCount: z.number().int().nonnegative(),
  eventCount: z.number().int().nonnegative(),
  /**
   * The features switched on for this company. Absent from the list is off.
   *
   * Defaulted, so a web build that goes live before the API it talks to — the
   * Worker deploys in seconds, the API takes minutes — reads an older response as
   * "no features" instead of rejecting every tenant page as the wrong shape.
   */
  features: z.array(tenantFeatureSchema).default([]),
  /**
   * Who pays for this company, or null for one that existed before billing and
   * has not had an owner chosen. Billing cannot go live while any company is
   * null. Defaulted for the same deploy-order reason as `features`.
   */
  billingOwner: billingOwnerDtoSchema.nullable().default(null),
  /** Where the company stands on billing. Null only from an API older than billing. */
  billing: billingSummaryDtoSchema.nullable().default(null),
})

export type TenantDto = z.infer<typeof tenantDtoSchema>

export const tenantListDtoSchema = z.object({
  tenants: z.array(tenantDtoSchema),
  total: z.number().int().nonnegative(),
})

export type TenantListDto = z.infer<typeof tenantListDtoSchema>

/** One time a feature was switched for a company. */
export const tenantFeatureChangeDtoSchema = z.object({
  id: z.string().uuid(),
  feature: tenantFeatureSchema,
  enabled: z.boolean(),
  /** Who switched it; null once that person's account is gone. */
  changedBy: z.object({ id: z.string().uuid(), name: z.string() }).nullable(),
  createdAt: instantSchema,
})

export type TenantFeatureChangeDto = z.infer<typeof tenantFeatureChangeDtoSchema>

export const tenantFeatureChangeListDtoSchema = z.object({
  changes: z.array(tenantFeatureChangeDtoSchema),
})

export type TenantFeatureChangeListDto = z.infer<typeof tenantFeatureChangeListDtoSchema>
